import React, { useState } from "react";
import { BsEye, BsEyeSlash } from "react-icons/bs";
import { Link, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import ApiService from "../../service/ApiService";
import { apiPaths } from "../../service/apiPaths";
import { GoogleLogin } from "@react-oauth/google";
import { useDispatch } from "react-redux";
import { setTokenInCookie, setUserInfo } from "../../redux/hejalSlice";

const SignUp = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    checked: false,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
    setErrors({ ...errors, [name]: "" });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.fullName) newErrors.fullName = "Enter your full name";
    if (!formData.email) newErrors.email = "Enter your email";
    else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(formData.email))
      newErrors.email = "Enter a valid email";
    if (!formData.phoneNumber)
      newErrors.phoneNumber = "Enter your phone number";
    else if (!/^\d{10,15}$/.test(formData.phoneNumber))
      newErrors.phoneNumber = "Enter a valid phone number";
    if (!formData.password) newErrors.password = "Create a password";
    else if (
      !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/.test(
        formData.password
      )
    )
      newErrors.password =
        "Minimum 6 characters, uppercase, number, special character";

    if (!formData.checked) newErrors.checked = "You must agree to the terms";

    return newErrors;
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (formData.confirmPassword !== formData.password) {
      toast.error("Passwords do not match");
      return;
    }
    setIsSubmitting(true);

    // Validate form data
    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      // Call API for signup
      const response = await ApiService({
        method: "POST",
        endpoint: apiPaths.Signup,
        data: formData,
      });

      toast.success(
        response?.message ||
        `Hello ${formData.fullName}, your signup was successful!`,
        {
          duration: 5000,
        }
      );

      setFormData({
        fullName: "",
        email: "",
        phoneNumber: "",
        password: "",
        confirmPassword: "",
        checked: false,
      });

      navigate("/verification", {
        state: {
          email: formData.email,
        },
      });
    } catch (error) {
      // Handle any unexpected errors
      console.error("Signup failed:", error);
      toast.error(error?.response?.data?.message || "Error Occurred", {
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = async (credentialResponse) => {
    try {
      setIsSubmitting(true);

      // Call your backend API with the Google token
      const response = await ApiService({
        method: "POST",
        endpoint: apiPaths.Google,
        data: {
          googleToken: credentialResponse.credential,
          path: "signup",
        },
      });


      // Store authentication tokens in cookies
      if (response?.data?.token) {
        dispatch(
          setTokenInCookie({
            token: response.data?.token,
            refreshToken: response.data?.refreshToken,
          })
        );
        // Store user info in Redux state
        console.log("store in redux",response.data.user);
        dispatch(setUserInfo(response.data.user));
        // Navigate to the phone number page
        navigate("/userverify");
      }
      toast.success(response?.message || "Google signup successful!", {
        duration: 5000,
      });

    } catch (error) {
      console.error("Google signup failed:", error);
      toast.error(error?.response?.data?.message || "Google login failed", {
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen mix-bg-light flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-5xl bg-white shadow-2xl rounded-2xl overflow-hidden grid grid-cols-1 lg:grid-cols-5">
        {/* Left Side - Branding Section */}
        <div className="lg:col-span-2  hidden bg-[#182134] md:flex flex-col justify-center items-center p-12 text-white">
          {/* <img
            src="https://firebasestorage.googleapis.com/v0/b/savadeshi-vidhyapith.appspot.com/o/Hejal%2FHEJALLOGO.webp?alt=media&token=dc945192-d340-4e52-8709-0a96a3314e93"
            alt="logo"
            className="w-40 mb-8"
          /> */}
          <h1 className="text-2xl font-bold text-white">
                   Luggaelegacy
                  </h1>
          <h2 className="text-3xl font-bold mb-4 text-center">Welcome to Luggaelegacy</h2>
          <p className="text-center opacity-80">
            Create your account and unlock a world of possibilities
          </p>
        </div>

        {/* Right Side - Signup Form */}
        <div className="lg:col-span-3  p-8 md:p-12 flex flex-col justify-center">
          <h1 className="text-3xl font-bold mb-2 text-gray-800">Create Account</h1>
          <p className="mb-6 text-gray-600">Enter your details to get started</p>

          <form 
          // onSubmit={handleSignUp} 
          className="space-y-5">
            <div className="grid md:grid-cols-2 gap-4">
              {/* Full Name Input */}
              <div className="relative">
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 peer"
                  placeholder="Full Name"
                />
                {errors.fullName && (
                  <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>
                )}
              </div>

              {/* Email Input */}
              <div className="relative">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 peer"
                  placeholder="Email Address"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>
            </div>

            {/* Phone Number Input */}
            <div className="relative">
              <input
                type="text"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Phone Number"
              />
              {errors.phoneNumber && (
                <p className="text-red-500 text-sm mt-1">{errors.phoneNumber}</p>
              )}
            </div>

            {/* Account Type Selection */}
            {/* <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Select Account Type
              </label>
              <div className="flex space-x-4">
                {["Retail Buyer"].map((type) => (
                  <label
                    key={type}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="accountType"
                      value={type.toLowerCase()}
                      checked={formData.accountType === type.toLowerCase()}
                      onChange={handleChange}
                      className="form-radio text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-gray-700">{type}</span>
                  </label>
                ))}
              </div>
              {errors.accountType && (
                <p className="text-red-500 text-sm mt-1">{errors.accountType}</p>
              )}
            </div> */}

            {/* Password Inputs */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Password */}
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Password"
                />
                <span
                  className="absolute right-3 top-4 cursor-pointer"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <BsEyeSlash size={20} /> : <BsEye size={20} />}
                </span>
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1">{errors.password}</p>
                )}
              </div>

              {/* Confirm Password */}
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Confirm Password"
                />
                <span
                  className="absolute right-3 top-4 cursor-pointer"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <BsEyeSlash size={20} /> : <BsEye size={20} />}
                </span>
              </div>
            </div>

            {/* Terms Checkbox */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="checked"
                checked={formData.checked}
                onChange={handleChange}
                className="form-checkbox rounded text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">
                I agree to the{" "}
                <Link to="/termscondition" className="text-blue-600 hover:underline">
                  terms and conditions
                </Link>
              </span>
            </div>
            {errors.checked && (
              <p className="text-red-500 text-sm">{errors.checked}</p>
            )}

            {/* Submit Button */}
            <button
              onClick={handleSignUp}
              disabled={isSubmitting}
              className={`w-full py-3 rounded-lg text-white font-semibold transition-all duration-300 
                ${isSubmitting
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#182134] hover:bg-secondColor"} `}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin h-5 w-5 mr-3"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                "Create Account"
              )}
            </button>

            {/* Google Login */}
            {/* <div className="flex justify-center">
              <GoogleLogin
                onSuccess={handleGoogleLogin}
                onError={(error) => {
                  console.log("Login Failed from google : ", error);
                  toast.error("Google login failed", { duration: 5000 });
                }}
              />
            </div> */}

            {/* Sign In Link */}
            <p className="text-center text-sm text-gray-600 mt-4">
              Already have an account?{" "}
              <Link to="/signin" className="text-blue-600 hover:underline">
                Sign In
              </Link>
            </p>
          </form>
        </div>
      </div>
      <Toaster />
    </div>
  );
};

export default SignUp;
